import axios from 'axios';

// Determine API URL based on environment
// In production (deployed), always use relative URL to ensure frontend talks to same-origin backend
// In development (preview), use environment variable if available
const getApiUrl = () => {
  // Check if we're in production (not on preview domain)
  const isProduction = typeof window !== 'undefined' && 
    !window.location.hostname.includes('preview.emergentagent.com') &&
    !window.location.hostname.includes('localhost');
  
  if (isProduction) {
    // In production, always use relative URL
    return '/api';
  }
  
  // In development/preview, use env var or fallback to relative
  return process.env.REACT_APP_BACKEND_URL 
    ? `${process.env.REACT_APP_BACKEND_URL}/api`
    : '/api';
};

const API_URL = getApiUrl();

// Log API configuration for debugging
console.log('[API Config]', {
  API_URL,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'SSR',
  env_var: process.env.REACT_APP_BACKEND_URL || 'not set'
});

const api = axios.create({ baseURL: API_URL });

// Request interceptor - log outgoing requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors with detailed logging
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    // Build detailed error info
    const errorInfo = {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      baseURL: error.config?.baseURL,
      data: error.response?.data,
      code: error.code
    };
    
    console.error('[API Error]', errorInfo);
    
    // Create user-friendly error message
    let userMessage = 'An error occurred';
    
    if (error.code === 'ERR_NETWORK') {
      userMessage = `Network Error: Cannot reach server at ${error.config?.baseURL}. Please check your connection.`;
    } else if (error.code === 'ERR_BAD_REQUEST') {
      userMessage = error.response?.data?.detail || 'Bad request';
    } else if (error.response?.status === 401) {
      userMessage = 'Session expired. Please login again.';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      userMessage = 'Access denied. You do not have permission.';
    } else if (error.response?.status === 404) {
      userMessage = `API endpoint not found: ${error.config?.url}`;
    } else if (error.response?.status === 500) {
      userMessage = 'Server error. Please try again later.';
    } else if (error.response?.status === 502 || error.response?.status === 503) {
      userMessage = 'Server is temporarily unavailable. Please try again.';
    } else if (error.response?.data?.detail) {
      userMessage = error.response.data.detail;
    }
    
    // Attach user-friendly message to error
    error.userMessage = userMessage;
    error.debugInfo = errorInfo;
    
    return Promise.reject(error);
  }
);

// Export API URL for debugging
export const getDebugInfo = () => ({
  API_URL,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'SSR',
  env_var: process.env.REACT_APP_BACKEND_URL || 'not set'
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getCaptcha: () => api.get('/auth/captcha'),
  confirmEmail: (data) => api.post('/auth/confirm-email', data),
  verifyOtp: (data) => api.post('/auth/verify-otp', data),
  resendOtp: (data) => api.post('/auth/resend-otp', data),
  me: () => api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password }),
  loginAudit: (params) => api.get('/auth/login-audit', { params }),
};

// Dashboard
export const dashboardAPI = {
  stats: () => api.get('/dashboard/stats'),
};

// Tools
export const toolsAPI = {
  list: (params) => api.get('/tools', { params }),
  get: (id) => api.get(`/tools/${id}`),
  create: (data) => api.post('/tools', data),
  update: (id, data) => api.put(`/tools/${id}`, data),
  delete: (id) => api.delete(`/tools/${id}`),
  eligibleItems: () => api.get('/instrument-eligible-items'),
};

export const mrpAPI = {
  run: (data) => api.post('/mrp/run', data),
  rerun: (id, data) => api.put(`/mrp/runs/${id}`, data),
  listRuns: () => api.get('/mrp/runs'),
  getRun: (id) => api.get(`/mrp/runs/${id}`),
  deleteRun: (id) => api.delete(`/mrp/runs/${id}`),
  exportRun: (id) => api.get(`/mrp/runs/${id}/export`, { responseType: 'blob' }),
  convertToIndent: (id, itemIds, deleteRun = true) => api.post(`/mrp/runs/${id}/convert-to-indent`, { item_ids: itemIds || null, delete_run: deleteRun }),
  planningCycles: () => api.get('/mrp/planning-cycles'),
  setDefaultBom: (item_id, route_id) => api.post('/mrp/set-default-bom', { item_id, route_id }),
  downloadTemplate: () => api.get('/mrp/template', { responseType: 'blob' }),
  runExcel: (formData, params) => api.post('/mrp/run-excel', formData, { params, headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Calibrations
export const calibrationsAPI = {
  list: (toolId) => api.get(`/calibrations/${toolId}`),
  create: (data) => api.post('/calibrations', data),
};

// Files
export const filesAPI = {
  upload: (formData) => api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  list: (entityType, entityId) => api.get(`/files/list/${entityType}/${entityId}`),
  getUrl: (fileId) => `${API_URL}/files/${fileId}`,
  toggleStatus: (fileId) => api.put(`/files/${fileId}/status`),
};

// Audit Logs
export const auditAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Calibration Report
export const calibrationReportAPI = {
  get: (params) => api.get('/calibration/report', { params }),
  exportMasterList: (params) => api.get('/calibration/master-list/export', { params, responseType: 'blob' }),
};

// Supplier Report
export const supplierReportAPI = {
  get: (params) => api.get('/reports/supplier', { params }),
};

// Users
export const usersAPI = {
  list: () => api.get('/users'),
  updateRole: (id, role) => api.put(`/users/${id}/role`, { role }),
  updateStatus: (id, action) => api.put(`/users/${id}/status?action=${action}`),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/users/${id}/reset-password`, { new_password: newPassword }),
  updateAppRoles: (id, appRoles) => api.put(`/users/${id}/app-roles`, { app_roles: appRoles }),
  updateContactEmail: (id, contactEmail) => api.put(`/users/${id}/contact-email`, { contact_email: contactEmail }),
  updateDesignation: (id, designation) => api.put(`/users/${id}/designation`, { designation }),
  updateOtpEmail: (id, otpEmail) => api.put(`/users/${id}/otp-email`, { otp_email: otpEmail }),
};

// ERP Apps
export const erpAppsAPI = {
  list: () => api.get('/erp-apps'),
};

// Notifications
export const notificationsAPI = {
  checkDue: () => api.post('/notifications/check-calibration-due'),
  list: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
};

// Quality Check (challan-based)
export const qualityAPI = {
  dashboard: () => api.get('/quality/dashboard'),
  listPending: (params) => api.get('/quality/pending', { params }),
  listHistory: (params) => api.get('/quality/history', { params }),
  decide: (data) => api.post('/quality/decide', data),
  closeInboundQC: (challanId, data) => api.post(`/quality/close-inbound-qc/${challanId}`, data),
  // Outbound QC (supplier returns from job work)
  listOutboundPending: (params) => api.get('/quality/outbound/pending', { params }),
  listOutboundHistory: (params) => api.get('/quality/outbound/history', { params }),
  receiveAndQC: (data) => api.post('/quality/outbound/receive-and-qc', data),
  // Receipt QC (items with perform_qc_on_receipt flag)
  listReceiptPending: (params) => api.get('/quality/receipt-pending', { params }),
  listReceiptHistory: (params) => api.get('/quality/receipt-history', { params }),
  receiptDecide: (data) => api.post('/quality/receipt-decide', data),
  correctReceiptItem: (data) => api.post('/quality/receipt-correct-item', data),
  voidReceipt: (data) => api.post('/quality/receipt-void', data),
  cleanupJobworkReceiptQC: (params) => api.post('/quality/cleanup-jobwork-receipt-qc', null, { params }),
};

// Material Review Board (MRB)
export const mrbAPI = {
  dashboard: () => api.get('/mrb/dashboard'),
  alerts: () => api.get('/mrb/alerts'),
  listCases: (params) => api.get('/mrb/cases', { params }),
  getCase: (id) => api.get(`/mrb/cases/${id}`),
  updateCustomerWO: (id, data) => api.patch(`/mrb/cases/${id}/customer-wo`, data),
  setDisposition: (id, data) => api.post(`/mrb/cases/${id}/disposition`, data),
  releaseReworkWO: (id) => api.post(`/mrb/cases/${id}/release-rework-wo`),
  approveReaccept: (id, data) => api.post(`/mrb/cases/${id}/approve-reaccept`, data),
  uploadCustomerAcceptance: (id, formData) => api.post(`/mrb/cases/${id}/upload-customer-acceptance`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadCustomerAcceptance: (id) => api.get(`/mrb/cases/${id}/customer-acceptance`, { responseType: 'blob' }),
  sendToCustomer: (id, data) => api.post(`/mrb/cases/${id}/send-to-customer`, data),
  recordCustomerDisposition: (id, formData) => api.post(`/mrb/cases/${id}/customer-disposition`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadCustomerDispositionFile: (id) => api.get(`/mrb/cases/${id}/customer-disposition-file`, { responseType: 'blob' }),
  // Notes
  addNote: (id, data) => api.post(`/mrb/cases/${id}/notes`, data),
  deleteNote: (id, noteId) => api.delete(`/mrb/cases/${id}/notes/${noteId}`),
  // Attachments
  uploadAttachment: (id, formData) => api.post(`/mrb/cases/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadAttachment: (id, fileId) => api.get(`/mrb/cases/${id}/attachments/${fileId}`, { responseType: 'blob' }),
  previewAttachment: (id, fileId) => api.get(`/mrb/cases/${id}/attachments/${fileId}/preview`, { responseType: 'blob' }),
  deleteAttachment: (id, fileId) => api.delete(`/mrb/cases/${id}/attachments/${fileId}`),
};

// Dashboard Analytics
export const analyticsAPI = {
  dashboard: () => api.get('/analytics/dashboard'),
};

// Suppliers
export const suppliersAPI = {
  list: (params) => api.get('/suppliers', { params }),
  get: (id) => api.get(`/suppliers/${id}`),
  create: (data) => api.post('/suppliers', data),
  update: (id, data) => api.put(`/suppliers/${id}`, data),
  delete: (id) => api.delete(`/suppliers/${id}`),
  approvedList: (params) => api.get('/suppliers/approved-list', { params }),
  dashboard: () => api.get(`/suppliers/dashboard?_t=${Date.now()}`),
  pendingReevaluation: () => api.get('/suppliers/pending-reevaluation'),
  startReevaluation: (id) => api.post(`/suppliers/${id}/start-reevaluation`),
  submitEvaluation: (id, data) => api.post(`/suppliers/${id}/submit-evaluation`, data),
  performance: (id, params = {}) => api.get(`/suppliers/${id}/performance`, { params }),
  performanceReport: (data) => api.post('/suppliers/performance-report', data),
  reevaluationHistory: (id) => api.get(`/suppliers/${id}/reevaluation-history`),
  checkReevaluationDue: () => api.post('/suppliers/check-reevaluation-due'),
  importExcel: (formData) => api.post('/suppliers/import-excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  importTemplate: () => api.get('/suppliers/import-template'),
  approveNonAs: (id) => api.put(`/suppliers/${id}/approve-non-as`),
  rejectNonAs: (id) => api.put(`/suppliers/${id}/reject-non-as`),
  exportAslReport: () => api.get('/suppliers/asl-report/export', { responseType: 'blob' }),
};

// Scopes of Approval
export const scopesAPI = {
  list: () => api.get('/scopes-of-approval'),
  create: (data) => api.post('/scopes-of-approval', data),
  delete: (id) => api.delete(`/scopes-of-approval/${id}`),
};

// Scheduler
export const schedulerAPI = {
  status: () => api.get('/scheduler/status'),
};

// Inventory
export const productionAPI = {
  getWipStock: (asOn) => api.get('/production/wip-stock', { params: asOn ? { as_on: asOn } : {} }),
  exportWipStock: (asOn) => api.get('/production/wip-stock/export', {
    params: asOn ? { as_on: asOn } : {}, responseType: 'blob',
  }),
};

export const emailSettingsAPI = {
  list: () => api.get('/email-settings'),
  update: (key, data) => api.put(`/email-settings/${key}`, data),
  reset: (key) => api.post(`/email-settings/${key}/reset`),
};

export const inventoryAPI = {  dashboard: () => api.get('/inventory/dashboard'),
  // Admin — production receipt / trace_lot cleanup
  listWoReceipts: (woId) => api.get(`/inventory/admin/wo/${woId}/receipts`),
  cancelProductionReceipt: (txnId, data) => api.post(`/inventory/admin/cancel-production-receipt/${txnId}`, data),
  listStaleFgTraceLots: (params) => api.get('/inventory/admin/stale-fg-trace-lots', { params }),
  deleteTraceLots: (data) => api.post('/inventory/admin/delete-trace-lots', data),
  inventoryTraceDrift: (params) => api.get('/inventory/admin/inventory-trace-drift', { params }),
  reconcileInventoryTraceDrift: (data) => api.post('/inventory/admin/reconcile-inventory-trace-drift', data),
  manifestStockDebug: (params) => api.get('/inventory/admin/manifest-stock-debug', { params }),
  traceLotSearch: (params) => api.get('/inventory/admin/trace-lot-search', { params }),
  editTraceLot: (data) => api.post('/inventory/admin/edit-trace-lot', data),
  autoFixStaleFgLots: (data) => api.post('/inventory/admin/auto-fix-stale-fg-lots', data),
  backfillReceiptQc: (data) => api.post('/inventory/admin/backfill-receipt-qc', data),
  reconcileSubcontractChallan: (data) => api.post('/inventory/admin/reconcile-subcontract-challan', data),
  // Categories
  listCategories: () => api.get('/inventory/categories'),
  cleanupCategories: () => api.post('/inventory/categories/cleanup'),
  createCategory: (data) => api.post('/inventory/categories', data),
  updateCategory: (id, data) => api.put(`/inventory/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/inventory/categories/${id}`),
  // Pending Approvals (Admin)
  getPendingApprovals: () => api.get('/inventory/pending-approvals'),
  listChangeRequests: (params) => api.get('/inventory/change-requests', { params }),
  approveChangeRequest: (id) => api.post(`/inventory/change-requests/${id}/approve`),
  rejectChangeRequest: (id, reason) => api.post(`/inventory/change-requests/${id}/reject`, { reason }),
  // Items
  listItems: (params) => api.get('/inventory/items', { params }),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  getItemChangelog: (id) => api.get(`/inventory/items/${id}/changelog`),
  bulkCopyItems: (itemIds) => api.post('/inventory/items/bulk-copy', { item_ids: itemIds }),
  duplicateSheetItem: (itemId, data) => api.post(`/inventory/items/${itemId}/duplicate-sheet`, data),
  listGrades: () => api.get('/inventory/grades'),
  createGrade: (name) => api.post('/inventory/grades', { name }),
  downloadItemTemplate: () => api.get('/inventory/item-template', { responseType: 'blob' }),
  importItemsExcel: (formData) => api.post('/inventory/items-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  suspendItem: (id) => api.patch(`/inventory/items/${id}/suspend`),
  approveItem: (id) => api.patch(`/inventory/items/${id}/approve`),
  rejectItem: (id, reason) => api.patch(`/inventory/items/${id}/reject`, { reason }),
  // Godowns
  listGodowns: () => api.get('/inventory/godowns'),
  backfillDefaultGodown: () => api.post('/inventory/items/backfill-default-godown'),
  backfillBomGodown: () => api.post('/inventory/routes/backfill-bom-godown'),
  createGodown: (data) => api.post('/inventory/godowns', data),
  updateGodown: (id, data) => api.put(`/inventory/godowns/${id}`, data),
  deleteGodown: (id) => api.delete(`/inventory/godowns/${id}`),
  // Job Work Items (for inbound subcontracting)
  listJobWorkItems: () => api.get('/inventory/items/job-work/list'),
  // Transactions
  listTransactions: (params) => api.get('/inventory/transactions', { params }),
  createTransaction: (data) => api.post('/inventory/transactions', data),
  getPendingWorkOrders: (itemId) => api.get('/inventory/pending-work-orders', { params: itemId ? { item_id: itemId } : {} }),
  // Calibration ↔ Subcontract Integration
  sendForCalibration: (params) => api.post('/inventory/calibration/send-for-calibration', null, { params }),
  receiveFromCalibration: (params) => api.post('/inventory/calibration/receive-from-calibration', null, { params }),
  // Stock
  getStock: (params) => api.get('/inventory/stock', { params }),
  // Stock Transfer (Admin)
  transferStock: (data) => api.post('/inventory/stock/transfer', data),
  listTransfers: (params) => api.get('/inventory/stock/transfers', { params }),
  // Stock Reconciliation (Admin)
  reconcileStock: (data) => api.post('/inventory/stock/reconcile', data),
  listReconciliations: (params) => api.get('/inventory/stock/reconciliations', { params }),
  // Routes
  listRoutes: (params) => api.get('/inventory/routes', { params }),
  getRoute: (id) => api.get(`/inventory/routes/${id}`),
  createRoute: (data) => api.post('/inventory/routes', data),
  updateRoute: (id, data) => api.put(`/inventory/routes/${id}`, data),
  deleteRoute: (id) => api.delete(`/inventory/routes/${id}`),
  suspendRoute: (id) => api.put(`/inventory/routes/${id}/suspend`),
  approveRoute: (id) => api.patch(`/inventory/routes/${id}/approve`),
  rejectRoute: (id, reason) => api.patch(`/inventory/routes/${id}/reject`, { reason }),
  // Work Centers
  listWorkCenters: () => api.get('/inventory/work-centers'),
  createWorkCenter: (data) => api.post('/inventory/work-centers', data),
  updateWorkCenter: (id, data) => api.put(`/inventory/work-centers/${id}`, data),
  deleteWorkCenter: (id) => api.delete(`/inventory/work-centers/${id}`),
  // Work Orders
  listWorkOrders: (params) => api.get('/inventory/work-orders', { params }),
  getWorkOrder: (id) => api.get(`/inventory/work-orders/${id}`),
  getWOChildPlan: (id) => api.get(`/inventory/work-orders/${id}/child-plan`),
  createChildWorkOrders: (id) => api.post(`/inventory/work-orders/${id}/create-children`),
  getWOTree: (id) => api.get(`/inventory/work-orders/${id}/tree`),
  releaseWOTree: (id) => api.post(`/inventory/work-orders/${id}/release-tree`),
  createWorkOrder: (data) => api.post('/inventory/work-orders', data),
  updateWorkOrder: (id, data) => api.put(`/inventory/work-orders/${id}`, data),
  deleteWorkOrder: (id) => api.delete(`/inventory/work-orders/${id}`),
  forceCloseWorkOrder: (id, data) => api.post(`/inventory/work-orders/${id}/force-close`, data),
  reopenWorkOrder: (id) => api.post(`/inventory/work-orders/${id}/reopen`),
  productionReceipt: (id, data) => api.post(`/inventory/work-orders/${id}/production-receipt`, data),
  getTraceLotsForWO: (id) => api.get(`/inventory/work-orders/${id}/trace-lots`),
  getTraceLots: (params) => api.get('/inventory/trace-lots', { params }),
  getTraceLifecycle: (traceId) => api.get(`/inventory/trace-lots/${encodeURIComponent(traceId)}/lifecycle`),
  traceSearch: (params) => api.get('/inventory/trace-search', { params }),
  traceLifecycleDetail: (id) => api.get(`/inventory/trace-search/${id}/lifecycle`),
  getChallanLineItemsForRoute: (routeId) => api.get(`/inventory/work-orders/challan-line-items-for-route/${routeId}`),
  getBomStoreLots: (routeId) => api.get(`/inventory/routes/${routeId}/bom-store-lots`),
  getPartPlanInfo: (itemId, quantity, routeId) => api.get('/inventory/production/part-plan-info', { params: { item_id: itemId, quantity, ...(routeId ? { route_id: routeId } : {}) } }),
  getItemRoutes: (itemId) => api.get('/inventory/production/item-routes', { params: { item_id: itemId } }),
  getLevel1Items: () => api.get('/inventory/production/level1-items'),
  createProductionPlan: (data) => api.post('/inventory/production/plans', data),
  listProductionPlans: (includeCompleted) => api.get('/inventory/production/plans', { params: { include_completed: !!includeCompleted } }),
  getProductionPlan: (id) => api.get(`/inventory/production/plans/${id}`),
  listRouteCards: (params) => api.get('/inventory/route-cards', { params }),
  getRouteCard: (rc) => api.get(`/inventory/route-cards/${encodeURIComponent(rc)}`),
  closeRouteCard: (rc, payload) => api.post(`/inventory/route-cards/${encodeURIComponent(rc)}/close`, payload),
  rejectionReasons: () => api.get('/inventory/rejection-reasons'),
  addRejectionReason: (name) => api.post('/inventory/rejection-reasons', { name }),
  deleteRejectionReason: (id) => api.delete(`/inventory/rejection-reasons/${id}`),
  rejectionReport: (params) => api.get('/inventory/reports/rejections', { params }),
  setRejectionTarget: (target_pct) => api.put('/inventory/reports/rejections/target', { target_pct }),
  rejectionItemDrilldown: (itemId, params) => api.get(`/inventory/reports/rejections/item/${itemId}`, { params }),
  exportRejectionReport: (params) => api.get('/inventory/reports/rejections/export', { params, responseType: 'blob' }),
  reopenRouteCard: (rc) => api.post(`/inventory/route-cards/${encodeURIComponent(rc)}/reopen`),
  releaseProductionPlan: (id) => api.post(`/inventory/production/plans/${id}/release`),
  releaseAndReceiveProductionPlan: (id) => api.post(`/inventory/production/plans/${id}/release-and-receive`),
  deleteProductionPlan: (id) => api.delete(`/inventory/production/plans/${id}`),
  cancelProductionPlan: (id, reason) => api.post(`/inventory/production/plans/${id}/cancel`, { reason }),
  updatePlanLineQty: (id, idx, data) => api.put(`/inventory/production/plans/${id}/lines/${idx}`, data),
  backfillChallanLineItems: () => api.post('/inventory/work-orders/backfill-challan-line-items'),
  // Scrap disposal
  getScrapItems: () => api.get('/inventory/scrap/items'),
  disposeScrap: (data) => api.post('/inventory/scrap/dispose', data),
  // Users with inventory access
  getInventoryUsers: () => api.get('/inventory/users'),
  // Item → Program linking
  getItemLinkedPrograms: (itemId) => api.get(`/inventory/items/${itemId}/linked-programs`),
  // Misc Receipts (Non-Inventory Spend Tracking)
  createMiscReceipt: (data) => api.post('/inventory/misc-receipts', data),
  listMiscReceipts: (params) => api.get('/inventory/misc-receipts', { params }),
  linkMiscReceiptProgram: (id, data) => api.put(`/inventory/misc-receipts/${id}/link-program`, data),
  miscReceiptSpendSummary: (params) => api.get('/inventory/misc-receipts/spend-summary', { params }),
  linkItemToProgram: (itemId, data) => api.post(`/inventory/items/${itemId}/link-to-program`, data),
  // FIFO stock lots
  getStockLots: (itemId, params) => api.get(`/inventory/items/${itemId}/stock-lots`, { params }),
  // Qty Discrepancy (Short/Excess)
  reportQtyDiscrepancy: (data) => api.post('/inventory/qty-discrepancy', data),
  listQtyDiscrepancies: (params) => api.get('/inventory/qty-discrepancies', { params }),
  sendDiscrepancyEmail: (discId, data) => api.post(`/inventory/qty-discrepancies/${discId}/send-email`, data),
  // Stock Aging Report
  getStockAging: (params) => api.get('/inventory/stock-aging', { params }),
  // Trace Consumption Report
  traceConsumptionReport: (q) => api.get('/inventory/trace-consumption-report', { params: { q } }),
};

// Item Files
export const itemFilesAPI = {
  list: (itemId) => api.get(`/inventory/items/${itemId}/files`),
  upload: (itemId, formData) => api.post(`/inventory/items/${itemId}/files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  download: (itemId, fileId) => api.get(`/inventory/items/${itemId}/files/${fileId}/download`, { responseType: 'blob' }),
  delete: (itemId, fileId) => api.delete(`/inventory/items/${itemId}/files/${fileId}`),
};

// Low Stock Report
export const lowStockAPI = {
  getReport: (itemType) => api.get('/inventory/low-stock-report', { params: itemType ? { item_type: itemType } : {} }),
  getReportByMakeOrBuy: (makeOrBuy) => api.get('/inventory/low-stock-report', { params: makeOrBuy ? { make_or_buy: makeOrBuy } : {} }),
  getItemTypes: () => api.get('/inventory/low-stock-report/item-types'),
  downloadPDF: (supplierId, itemType, makeOrBuy) => api.get('/inventory/low-stock-report/pdf', {
    params: { 
      ...(supplierId ? { supplier_id: supplierId } : {}), 
      ...(itemType ? { item_type: itemType } : {}),
      ...(makeOrBuy ? { make_or_buy: makeOrBuy } : {}),
    },
    responseType: 'blob',
  }),
  emailSupplier: (supplierId) => api.post(`/inventory/low-stock-report/email/${supplierId}`),
};

// Purchase Module
export const purchaseAPI = {
  dashboard: () => api.get('/purchase/dashboard'),
  // Indent
  addToIndent: (data) => api.post('/purchase/indent/add', data),
  addBulkToIndent: (items) => api.post('/purchase/indent/add-bulk', items),
  listIndent: (params) => api.get('/purchase/indent', { params }),
  removeFromIndent: (id) => api.delete(`/purchase/indent/${id}`),
  mergeIndentLines: (indentIds) => api.post('/purchase/indent/merge', { indent_ids: indentIds }),
  unmergeIndentLine: (id) => api.post(`/purchase/indent/${id}/unmerge`),
  updateIndentItem: (id, data) => api.put(`/purchase/indent/${id}`, data),
  // Auto-Indent
  autoIndentPreview: () => api.get('/purchase/auto-indent/preview'),
  autoIndentRun: () => api.post('/purchase/auto-indent/run'),
  // RFQ
  sendRFQ: (data) => api.post('/purchase/rfq/send', data),
  sendRFQWithAttachments: (formData) => api.post('/purchase/rfq/send-with-attachments', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Purchase Requests
  createPurchaseRequest: (data) => api.post('/purchase/purchase-request', data),
  listPurchaseRequests: (params) => api.get('/purchase/purchase-requests', { params }),
  getPurchaseRequest: (id) => api.get(`/purchase/purchase-requests/${id}`),
  approvePurchaseRequest: (id, data) => api.put(`/purchase/purchase-requests/${id}/approve`, data),
  editPurchaseRequest: (id, data) => api.put(`/purchase/purchase-requests/${id}/edit`, data),
  ctsLinkCheck: (data) => api.post('/purchase/cts-link-check', data),
  getDimensionVariances: (params) => api.get('/purchase/dimension-variances', { params }),
  resendPRForPO: (id, data) => api.post(`/purchase/purchase-requests/${id}/resend-for-po`, data),
  getPREmailLogs: (id) => api.get(`/purchase/purchase-requests/${id}/email-logs`),
  approvePurchaseRequestWithQuote: (id, formData) => api.post(`/purchase/purchase-requests/${id}/approve-with-quote`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // PO Generation & Sending
  generatePO: (prId, data) => api.put(`/purchase/purchase-requests/${prId}/generate-po`, data),
  uploadPOFile: (prId, formData) => api.post(`/purchase/purchase-requests/${prId}/upload-po-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadPOFile: (prId, fileId) => api.get(`/purchase/purchase-requests/${prId}/po-file/${fileId}`, { responseType: 'blob' }),
  uploadQuoteFile: (prId, formData) => api.post(`/purchase/purchase-requests/${prId}/upload-quote`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadQuoteFile: (prId, fileId) => api.get(`/purchase/purchase-requests/${prId}/quote-file/${fileId}`, { responseType: 'blob' }),
  sendPO: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/send-po`, data),
  cancelPO: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/cancel`, data),
  shortClosePO: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/short-close`, data),
  // Open POs for receipt
  listOpenPOs: () => api.get('/purchase/open-pos'),
  getPOReceiptStatus: (prId) => api.get(`/purchase/${prId}/receipt-status`),
  getItemsProcurementStatus: (itemIds) => api.post('/purchase/items-procurement-status', { item_ids: itemIds }),
  itemsPriceHistory: (itemIds) => api.post('/purchase/items-price-history', { item_ids: itemIds }),
  receiveAgainstPO: (prId, items) => api.post(`/purchase/receive-against-po/${prId}`, items),
  closePOLine: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/close-line`, data),
  receiveCTS: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/receive-cts`, data),
  reconcileCTS: (prId, data) => api.post(`/purchase/purchase-requests/${prId}/reconcile-cts`, data),
  ctsReconcileInfo: (prId) => api.get(`/purchase/${prId}/cts-reconcile-info`),
  listCTSReconcilePending: () => api.get('/purchase/cts-reconcile/pending'),
  listCTSReconciliations: (prId) => api.get('/purchase/cts-reconciliations', { params: prId ? { pr_id: prId } : {} }),
  listCTSItemOptions: () => api.get('/purchase/cts-item-options'),
  getPurchaseSettings: () => api.get('/purchase/settings'),
  updatePurchaseSettings: (data) => api.put('/purchase/settings', data),
  undoCTSReconciliation: (recoId) => api.post(`/purchase/cts-reconciliations/${recoId}/undo`),
  approveCTSReceipt: (prId, receiptId) => api.post(`/purchase/purchase-requests/${prId}/approve-cts-receipt/${receiptId}`),
  rejectCTSReceipt: (prId, receiptId, data) => api.post(`/purchase/purchase-requests/${prId}/reject-cts-receipt/${receiptId}`, data),
  listCTSApprovals: () => api.get('/purchase/cts-approvals'),
  // Lookups for direct PR
  getSuppliers: () => api.get('/purchase/suppliers'),
  getItems: (q) => api.get('/purchase/items', { params: q ? { q } : {} }),
  // Analytics
  getSpendAnalytics: (params) => api.get('/purchase/analytics/spend', { params }),
};

// Subcontract (Multi-item Challans)
export const subcontractAPI = {
  dashboard: () => api.get('/subcontract/dashboard'),
  getDashboardKPIs: () => api.get('/subcontract/dashboard'),
  listChallans: (params) => api.get('/subcontract/challans', { params }),
  getChallan: (id) => api.get(`/subcontract/challans/${id}`),
  createChallan: (data) => api.post('/subcontract/challans', data),
  downloadImportTemplate: () => api.get('/subcontract/import/template', { responseType: 'blob' }),
  importChallanItems: (formData) => api.post('/subcontract/import/items', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  notifyLowStockCustomers: (params) => api.post('/subcontract/notify-low-stock-customers', null, { params }),
  sendChallan: (id) => api.put(`/subcontract/challans/${id}/send`),
  sendChallanWithEmail: (id, data) => api.put(`/subcontract/challans/${id}/send-with-email`, data),
  getSupplierEmail: (id) => api.get(`/subcontract/challans/${id}/supplier-email`),
  deleteChallan: (id) => api.delete(`/subcontract/challans/${id}`),
  receiveChallan: (id, data) => api.post(`/subcontract/challans/${id}/receive`, data),
  downloadChallanPDF: (id) => api.get(`/subcontract/challans/${id}/pdf`, { responseType: 'blob' }),
  getRecalledInstruments: (supplierId) => api.get(`/subcontract/recalled-instruments/${supplierId}`),
  // PO Linking
  linkPO: (challanId, data) => api.put(`/subcontract/challans/${challanId}/link-po`, data),
  getPOsForLinking: (params) => api.get('/subcontract/purchase-orders/for-linking', { params }),
  // Inbound job work integration
  getInboundDispatchStatus: (challanId) => api.get(`/subcontract/inbound/${challanId}/dispatch-status`),
  getOutboundReceiptStatus: (challanId) => api.get(`/subcontract/outbound/${challanId}/receipt-status`),
  closeInboundChallan: (challanId, data) => api.post(`/subcontract/inbound/${challanId}/close`, data || {}),
  getOpenInboundChallans: (customerId) => api.get(`/subcontract/inbound/open-for-customer/${customerId}`),
  getExpectedOutputItems: (challanId) => api.get(`/subcontract/inbound/${challanId}/expected-output-items`),
  getJobWorkDashboard: (params) => api.get('/subcontract/job-work-dashboard', { params }),
  getCustomerPOs: (customerId) => api.get(`/subcontract/customer-pos/${customerId}`),
  // Service Entry & Closure
  createServiceEntry: (challanId, data) => api.post(`/subcontract/challans/${challanId}/service-entry`, data),
  listServiceEntries: (challanId) => api.get(`/subcontract/challans/${challanId}/service-entries`),
  closeOutboundChallan: (challanId, data) => api.put(`/subcontract/challans/${challanId}/close`, data || {}),
  reopenChallan: (challanId, data) => api.post(`/subcontract/challans/${challanId}/reopen`, data),
  // Admin: Backfill Customer DC/PO on inbound challans
  listMissingCustomerRefs: (params) => api.get('/subcontract/admin/inbound-missing-customer-refs', { params }),
  updateChallanCustomerRefs: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/customer-refs`, data),
  // Admin: correct inbound challan entry (trace id / received-before-QC quantity)
  updateTraceability: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/update-traceability`, data),
  correctReceivedQty: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/correct-received-qty`, data),
  correctItemDueDate: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/correct-item-due-date`, data),
  correctChallanItem: (challanId, data) => api.patch(`/subcontract/challans/${challanId}/correct-item`, data),
  // Inbound challans available for closure
  listInboundReadyForClosure: (params) => api.get('/subcontract/inbound-ready-for-closure', { params }),
  // Customer material traceability report (WO / DC / PO)
  traceabilityReport: (params) => api.get('/subcontract/traceability-report', { params }),
  exportTraceabilityReport: (params) => api.get('/subcontract/traceability-report/export', { params, responseType: 'blob' }),
};

// Contract Management
export const contractsAPI = {
  dashboard: () => api.get('/contracts/dashboard'),
  // Customers
  listCustomers: (params) => api.get('/contracts/customers', { params }),
  activeCustomers: () => api.get('/contracts/customers/active-list'),
  getCustomer: (id) => api.get(`/contracts/customers/${id}`),
  createCustomer: (data) => api.post('/contracts/customers', data),
  updateCustomer: (id, data) => api.put(`/contracts/customers/${id}`, data),
  suspendCustomer: (id) => api.put(`/contracts/customers/${id}/suspend`),
  deleteCustomer: (id) => api.delete(`/contracts/customers/${id}`),
  // Purchase Orders
  listPurchaseOrders: (params) => api.get('/contracts/purchase-orders', { params }),
  listOpenPOs: (params) => api.get('/contracts/purchase-orders-open', { params }),
  getPurchaseOrder: (id) => api.get(`/contracts/purchase-orders/${id}`),
  createPurchaseOrder: (data) => api.post('/contracts/purchase-orders', data),
  updatePurchaseOrder: (id, data) => api.put(`/contracts/purchase-orders/${id}`, data),
  closePurchaseOrder: (id, data) => api.post(`/contracts/purchase-orders/${id}/close`, data),
  reopenPurchaseOrder: (id, data) => api.post(`/contracts/purchase-orders/${id}/reopen`, data),
  // PO Files
  uploadPOFile: (poId, formData) => api.post(`/contracts/purchase-orders/${poId}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadPOFile: (poId, fileId) => api.get(`/contracts/purchase-orders/${poId}/files/${fileId}/download`, { responseType: 'blob' }),
  deletePOFile: (poId, fileId) => api.delete(`/contracts/purchase-orders/${poId}/files/${fileId}`),
  // Contract Review
  sendAcknowledgment: (poId, body) => api.post(`/contracts/purchase-orders/${poId}/send-acknowledgment`, body),
  resolveEmails: (poId) => api.get(`/contracts/purchase-orders/${poId}/resolve-emails`),
  uploadReviewDocument: (poId, formData) => api.post(`/contracts/purchase-orders/${poId}/review/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  closeReview: (poId) => api.put(`/contracts/purchase-orders/${poId}/review/close`),
  reopenReview: (poId) => api.put(`/contracts/purchase-orders/${poId}/review/reopen`),
  pendingReviews: () => api.get('/contracts/reviews/pending'),
  // On-Time Delivery Report
  otdReport: (params) => api.get('/contracts/otd-report', { params }),
  otdReportExport: (params) => api.get('/contracts/otd-report/export', { params, responseType: 'blob' }),
};

// ======================== DISPATCH API ========================
export const dispatchAPI = {
  dashboard: () => api.get('/dispatch/dashboard'),
  listChallans: (params) => api.get('/dispatch/challans', { params }),
  getChallan: (id) => api.get(`/dispatch/challans/${id}`),
  createChallan: (data) => api.post('/dispatch/challans', data),
  // Dispatch Plan (auto work-order explosion)
  previewDispatchPlan: (data) => api.post('/dispatch/plans/preview', data),
  createDispatchPlan: (data) => api.post('/dispatch/plans', data),
  getDispatchItemWeights: (itemIds) => api.get('/dispatch/plans/item-weights', { params: { item_ids: itemIds } }),
  getCustomerDispatchEmails: (customerId) => api.get(`/dispatch/plans/customer-emails/${customerId}`),
  updateDispatchPlan: (planId, data) => api.put(`/dispatch/plans/${planId}`, data),
  listDispatchPlans: (includeDispatched) => api.get('/dispatch/plans', { params: { include_dispatched: !!includeDispatched } }),
  getDispatchPlan: (id) => api.get(`/dispatch/plans/${id}`),
  releaseDispatchPlan: (id) => api.post(`/dispatch/plans/${id}/release`),
  dispatchPlanPostingPreview: (id) => api.get(`/dispatch/plans/${id}/dispatch-preview`),
  produceAndDispatchPlan: (id, data) => api.post(`/dispatch/plans/${id}/produce-and-dispatch`, data || {}),
  deleteDispatchPlan: (id) => api.delete(`/dispatch/plans/${id}`),
  reconcileChallan: (id, data) => api.post(`/dispatch/challans/${id}/reconcile`, data),
  downloadPDF: (id) => api.get(`/dispatch/challans/${id}/pdf`, { responseType: 'blob' }),
  getPendingScrap: (params) => api.get('/dispatch/scrap-pending', { params }),
  // Invoice attachment
  uploadInvoice: (challanId, formData) => api.post(`/dispatch/challans/${challanId}/upload-invoice`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadInvoice: (challanId) => api.get(`/dispatch/challans/${challanId}/invoice`, { responseType: 'blob' }),
  downloadCOC: (challanId, { itemIndex = 0, rmLotOverride, customerPoOverride, qtyDispatchedOverride, qtyAcceptedOverride, attach = false, fmt = 'pdf' } = {}) => {
    const params = { item_index: itemIndex, fmt };
    if (rmLotOverride) params.rm_lot_override = rmLotOverride;
    if (customerPoOverride) params.customer_po_override = customerPoOverride;
    if (qtyDispatchedOverride !== undefined && qtyDispatchedOverride !== null && qtyDispatchedOverride !== '') params.qty_dispatched_override = qtyDispatchedOverride;
    if (qtyAcceptedOverride !== undefined && qtyAcceptedOverride !== null && qtyAcceptedOverride !== '') params.qty_accepted_override = qtyAcceptedOverride;
    if (attach) params.attach = true;
    return api.get(`/dispatch/challans/${challanId}/coc`, { params, responseType: 'blob' });
  },
  listCocLines: (challanId) => api.get(`/dispatch/challans/${challanId}/coc-lines`),
  downloadCOCBundle: (challanId, { fmt = 'pdf', itemIndices, attach = false } = {}) => {
    const params = { fmt };
    if (Array.isArray(itemIndices) && itemIndices.length) params.item_indices = itemIndices.join(',');
    if (attach) params.attach = true;
    return api.get(`/dispatch/challans/${challanId}/coc-bundle`, { params, responseType: 'blob' });
  },
  listCocAttachments: (challanId) => api.get(`/dispatch/challans/${challanId}/coc-attachments`),
  downloadCocAttachment: (challanId, attachId) => api.get(`/dispatch/challans/${challanId}/coc-attachments/${attachId}/download`, { responseType: 'blob' }),
  // Manifests
  listManifests: (params) => api.get('/dispatch/manifests', { params }),
  getManifest: (id) => api.get(`/dispatch/manifests/${id}`),
  createManifest: (data) => api.post('/dispatch/manifests', data),
  previewManifest: (data) => api.post('/dispatch/manifests/preview', data),
  downloadManifestPDF: (id) => api.get(`/dispatch/manifests/${id}/pdf`, { responseType: 'blob' }),
  emailManifest: (id, data) => api.post(`/dispatch/manifests/${id}/email`, data),
  createChallansFromManifest: (id) => api.post(`/dispatch/manifests/${id}/create-challans`),
  deleteManifest: (id) => api.delete(`/dispatch/manifests/${id}`),
  // Scrap Tracking
  scrapDashboard: () => api.get('/dispatch/scrap/dashboard'),
  listPendingScrap: (params) => api.get('/dispatch/scrap/pending', { params }),
  recordScrap: (data) => api.post('/dispatch/scrap/record', data),
  // Customer dispatch reports
  getCustomerDispatchedItems: (params) => api.get('/dispatch/reports/customer-dispatched-items', { params }),
  exportDispatchPlan: (data) => api.post('/dispatch/reports/dispatch-plan', data, { responseType: 'blob' }),
  getCustomerRmJwItems: (params) => api.get('/dispatch/reports/customer-rm-jw-items', { params }),
  exportRmJwStock: (data) => api.post('/dispatch/reports/rm-jw-stock', data, { responseType: 'blob' }),
  exportDispatchHistory: (data) => api.post('/dispatch/reports/customer-dispatch-history', data, { responseType: 'blob' }),
  updateScrapEntry: (id, data) => api.put(`/dispatch/scrap/${id}`, null, { params: data }),
  listScrapReturns: (params) => api.get('/dispatch/scrap/returns', { params }),
  createScrapReturn: (data) => api.post('/dispatch/scrap/returns', data),
  downloadScrapReturnPDF: (id) => api.get(`/dispatch/scrap/returns/${id}/pdf`, { responseType: 'blob' }),
  // Post-Dispatch Customer Email
  emailToCustomer: (data) => api.post('/dispatch/email-to-customer', data),
  oneClickDispatch: (manifestId, data) => api.post(`/dispatch/manifests/${manifestId}/one-click-dispatch`, data),
  getManifestRecipients: (manifestId) => api.get(`/dispatch/manifests/${manifestId}/recipient-emails`),
  // Supplier Return
  supplierReturnPreview: (data) => api.post('/dispatch/supplier-return/preview', data),
  supplierReturnCreateManifest: (data) => api.post('/dispatch/supplier-return/create-manifest', data),
  supplierReturnCreateChallans: (manifestId) => api.post(`/dispatch/supplier-return/manifests/${manifestId}/create-challans`),
  // Legacy fix
  backfillCustomerInfo: () => api.post('/dispatch/challans/backfill-customer-info'),
  // Admin: audit + correct customer DC# / PO# on existing manifests / DCs
  customerRefAudit: (params) => api.get('/dispatch/admin/customer-ref-audit', { params }),
  customerRefCorrect: (data) => api.post('/dispatch/admin/customer-ref-correct', data),
  auditPrematurelyClosedPOs: (params) => api.get('/dispatch/admin/audit-prematurely-closed-pos', { params }),
  reopenPrematurelyClosedPOs: (data) => api.post('/dispatch/admin/reopen-prematurely-closed-pos', data),
  // Admin: cancel a manifest (and its linked DCs), restoring stock + trace_lots
  listCancellableManifests: (params) => api.get('/dispatch/admin/cancellable-manifests', { params }),
  cancelManifest: (manifestId, data) => api.post(`/dispatch/admin/cancel-manifest/${manifestId}`, data),
  // Admin: cancel a single delivery challan, restoring stock + trace_lots
  cancelChallan: (challanId, data) => api.post(`/dispatch/challans/${challanId}/cancel`, data),
  // Dispatch Tag
  downloadDispatchTag: (manifestId) => api.get(`/dispatch/manifests/${manifestId}/dispatch-tag`, { responseType: 'blob' }),
};

// ======================== PROGRAMS API ========================
export const programsAPI = {
  list: (params) => api.get('/programs', { params }),
  get: (id) => api.get(`/programs/${id}`),
  create: (data) => api.post('/programs', data),
  update: (id, data) => api.put(`/programs/${id}`, data),
  delete: (id) => api.delete(`/programs/${id}`),
  analytics: () => api.get('/programs/analytics'),
  financials: (id) => api.get(`/programs/${id}/financials`),
  // PO linking
  linkPO: (id, formData) => api.post(`/programs/${id}/link-po`, formData),
  unlinkPO: (id, poId) => api.delete(`/programs/${id}/unlink-po/${poId}`),
  // Items
  addItem: (id, data) => api.post(`/programs/${id}/items`, data),
  updateItem: (id, itemId, data) => api.put(`/programs/${id}/items/${itemId}`, data),
  removeItem: (id, itemId) => api.delete(`/programs/${id}/items/${itemId}`),
  // Tasks
  createTask: (id, data) => api.post(`/programs/${id}/tasks`, data),
  updateTask: (id, taskId, data) => api.put(`/programs/${id}/tasks/${taskId}`, data),
  deleteTask: (id, taskId) => api.delete(`/programs/${id}/tasks/${taskId}`),
  // Cross-program tasks
  myTasks: (params) => api.get('/programs/my-tasks/list', { params }),
  taskDashboard: () => api.get('/programs/task-dashboard/analytics'),
  // Files
  uploadFile: (id, formData) => api.post(`/programs/${id}/files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadFile: (id, fileId) => api.get(`/programs/${id}/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (id, fileId) => api.delete(`/programs/${id}/files/${fileId}`),
  // Price sync (dynamic budget ↔ actuals reconciliation)
  syncPrices: (id, thresholdPct = 0) => api.post(`/programs/${id}/sync-prices`, null, { params: { threshold_pct: thresholdPct } }),
  // Admin: item linkage conflicts (items linked to > 1 program)
  listLinkageConflicts: () => api.get('/programs/admin/item-linkage-conflicts'),
  resolveLinkageConflict: (data) => api.post('/programs/admin/item-linkage-conflicts/resolve', data),
};

// Help API
export const helpAPI = {
  getCategories: () => api.get('/help/categories'),
  getCategoryArticles: (categoryId) => api.get(`/help/categories/${categoryId}`),
  getArticle: (categoryId, articleId) => api.get(`/help/articles/${categoryId}/${articleId}`),
  search: (q) => api.get('/help/search', { params: { q } }),
};

// Reports API
export const reportsAPI = {
  exportTableXlsx: (data) => api.post('/reports/export-xlsx', data, { responseType: 'blob' }),
  updateReceiptLineRate: (data) => api.patch('/reports/purchase-receipt-line-rate', data),
  programPnl: (params) => api.get('/reports/program-pnl', { params }),
  programPnlExport: (params, format) => api.get('/reports/program-pnl', {
    params: { ...params, format },
    responseType: 'blob',
  }),
};

export default api;
